import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface Measurement {
  id: string;
  estimate_id: string;
  measurement_type: string;
  area_sqft: number | null;
  pitch: string | null;
  layers: number | null;
  valley_lf: number | null;
  ridge_lf: number | null;
  rake_lf: number | null;
  eave_lf: number | null;
  hip_lf: number | null;
  flashing_lf: number | null;
  skylight_count: number;
  chimney_count: number;
  vent_count: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const useMeasurements = (estimateId?: string) => {
  const queryClient = useQueryClient();

  const { data: measurements, isLoading } = useQuery({
    queryKey: ["measurements", estimateId],
    queryFn: async () => {
      if (!estimateId) return [];
      
      const { data, error } = await supabase
        .from("measurements")
        .select("*")
        .eq("estimate_id", estimateId);

      if (error) throw error;
      return data as Measurement[];
    },
    enabled: !!estimateId,
  });

  const createMeasurement = useMutation({
    mutationFn: async (measurement: Partial<Measurement>) => {
      const { data, error } = await supabase
        .from("measurements")
        .insert([measurement as any])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["measurements"] });
      toast({ title: "Measurement saved" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save measurement",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMeasurement = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Measurement> & { id: string }) => {
      const { data, error } = await supabase
        .from("measurements")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["measurements"] });
      toast({ title: "Measurement updated" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update measurement",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    measurements,
    isLoading,
    createMeasurement,
    updateMeasurement,
  };
};
